import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  FileText, 
  Send,
  Plus,
  Users,
  PoundSterling,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Copy,
  ExternalLink,
  ChevronRight,
  Star,
  Zap,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RFQManagementPage() {
  const { rfqId } = useParams();
  const navigate = useNavigate();
  
  const [rfq, setRfq] = useState(null);
  const [contractorQuotes, setContractorQuotes] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [inviting, setInviting] = useState(false);
  
  const [showCreateOptionDialog, setShowCreateOptionDialog] = useState(false);
  const [selectedQuoteForOption, setSelectedQuoteForOption] = useState(null);
  const [optionForm, setOptionForm] = useState({
    option_id: 'A',
    title: '',
    description: '',
    badge: ''
  });
  const [createdOptions, setCreatedOptions] = useState([]);
  
  const [creatingQuote, setCreatingQuote] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadRFQ = useCallback(async () => {
    if (!rfqId) return;
    
    setLoading(true);
    try {
      const [rfqRes, quotesRes, contractorsRes] = await Promise.all([
        axios.get(`${API}/api/phs/rfq/${rfqId}`, { headers }),
        axios.get(`${API}/api/phs/rfq/${rfqId}/quotes`, { headers }),
        axios.get(`${API}/api/contractors?limit=100`, { headers }).catch(() => ({ data: { contractors: [] } }))
      ]);
      
      setRfq(rfqRes.data);
      setContractorQuotes(quotesRes.data?.quotes || []);
      setContractors(contractorsRes.data?.contractors || []);
    } catch (error) {
      console.error('Error loading RFQ:', error);
      toast.error('Failed to load RFQ details');
    } finally {
      setLoading(false);
    }
  }, [rfqId]);

  useEffect(() => {
    loadRFQ();
  }, [loadRFQ]);

  const handleInviteContractors = async () => {
    if (selectedContractors.length === 0) {
      toast.error('Please select at least one contractor');
      return;
    }
    
    setInviting(true);
    try {
      const res = await axios.post(
        `${API}/api/phs/rfq/${rfqId}/invite-contractors`,
        { contractor_ids: selectedContractors },
        { headers }
      );
      
      toast.success(`Invited ${res.data.invited} contractors`);
      setShowInviteDialog(false);
      setSelectedContractors([]);
      loadRFQ();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite contractors');
    } finally {
      setInviting(false);
    }
  };

  const handleCreateOption = (quote) => {
    setSelectedQuoteForOption(quote);
    setOptionForm({
      option_id: createdOptions.length === 0 ? 'A' : 'B',
      title: quote.contractor_preferred ? 'Premium Repair Solution' : 'Standard Repair Solution',
      description: '',
      badge: quote.contractor_preferred ? 'Recommended' : (createdOptions.length === 0 ? 'Best Value' : 'Alternative')
    });
    setShowCreateOptionDialog(true);
  };

  const handleAddOption = () => {
    if (!optionForm.title) {
      toast.error('Please enter a title for the option');
      return;
    }
    
    const newOption = {
      ...optionForm,
      contractor_quote_id: selectedQuoteForOption.contractor_quote_id,
      contractor_name: selectedQuoteForOption.contractor_name,
      internal_cost: selectedQuoteForOption.quote_total,
      client_price: selectedQuoteForOption.pricing?.client_price,
      lead_time: selectedQuoteForOption.lead_time,
      warranty_period: selectedQuoteForOption.warranty_period
    };
    
    setCreatedOptions([...createdOptions, newOption]);
    setShowCreateOptionDialog(false);
    toast.success(`Option ${optionForm.option_id} created`);
  };

  const handleCreatePHSQuote = async () => {
    if (createdOptions.length === 0) {
      toast.error('Please create at least one option');
      return;
    }
    
    setCreatingQuote(true);
    try {
      const res = await axios.post(
        `${API}/api/phs/quotes/create-multi-option`,
        {
          task_id: rfq.task_id,
          rfq_id: rfq.rfq_id,
          options: createdOptions.map(opt => ({
            option_id: opt.option_id,
            title: opt.title,
            description: opt.description,
            contractor_quote_id: opt.contractor_quote_id,
            badge: opt.badge
          })),
          markup_percentage: 25.0
        },
        { headers }
      );
      
      toast.success(`PHS Quote ${res.data.quote_id} created with ${res.data.options} option(s)`);
      navigate(`/phs-members?tab=quotes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PHS quote');
    } finally {
      setCreatingQuote(false);
    }
  };

  const copyInviteLink = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Invite link copied to clipboard');
  };

  const getStatusBadge = (status) => {
    const configs = {
      draft: { color: 'bg-gray-100 text-gray-600', label: 'Draft' },
      open: { color: 'bg-blue-100 text-blue-700', label: 'Open' },
      partially_quoted: { color: 'bg-amber-100 text-amber-700', label: 'Partial' },
      quoted: { color: 'bg-green-100 text-green-700', label: 'All Quoted' },
      closed: { color: 'bg-gray-100 text-gray-600', label: 'Closed' }
    };
    const config = configs[status] || configs.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-[#0F766E]" />
        </div>
      </Layout>
    );
  }

  if (!rfq) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-red-500 mb-4" />
          <p className="text-gray-600">RFQ not found</p>
        </div>
      </Layout>
    );
  }

  const invitedIds = (rfq.invited_contractors || []).map(i => i.contractor_id);
  const availableContractors = contractors.filter(c => !invitedIds.includes(c.id) && !invitedIds.includes(c.name));

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="rfq-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="h-6 w-6 text-[#0F766E]" />
                {rfq.rfq_id}
              </h1>
              <p className="text-gray-500 text-sm">
                Task: {rfq.task_id} • {rfq.trade_required}
              </p>
            </div>
          </div>
          {getStatusBadge(rfq.status)}
        </div>

        {/* RFQ Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Request Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-500">Trade Required</Label>
                <p className="font-medium">{rfq.trade_required}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Deadline</Label>
                <p className="font-medium">{new Date(rfq.deadline).toLocaleDateString('en-GB')}</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs text-gray-500">Description</Label>
                <p className="text-gray-700">{rfq.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Invited Contractors */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="h-4 w-4 text-[#0F766E]" />
                Invited Contractors ({rfq.invited_contractors?.length || 0})
              </CardTitle>
              <Button size="sm" onClick={() => setShowInviteDialog(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Invite More
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {rfq.invited_contractors?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contractor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rfq.invited_contractors.map((inv) => (
                    <TableRow key={inv.contractor_id}>
                      <TableCell className="font-medium">{inv.contractor_name}</TableCell>
                      <TableCell>
                        {inv.quote_submitted ? (
                          <Badge className="bg-green-100 text-green-700">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Quoted
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="h-3 w-3 mr-1" />
                            Awaiting
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(inv.invited_at).toLocaleDateString('en-GB')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => copyInviteLink(inv.invite_url)}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Link
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-gray-500 text-center py-6">No contractors invited yet</p>
            )}
          </CardContent>
        </Card>

        {/* Received Quotes - Internal View */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <PoundSterling className="h-4 w-4 text-[#0F766E]" />
              Contractor Quotes ({contractorQuotes.length})
              <Badge variant="outline" className="ml-2 text-xs">INTERNAL VIEW</Badge>
            </CardTitle>
            <CardDescription>Compare quotes and create member-facing options</CardDescription>
          </CardHeader>
          <CardContent>
            {contractorQuotes.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Contractor</TableHead>
                    <TableHead>Tier</TableHead>
                    <TableHead>Internal Cost</TableHead>
                    <TableHead>Client Price (25%)</TableHead>
                    <TableHead>Margin</TableHead>
                    <TableHead>Lead Time</TableHead>
                    <TableHead>Warranty</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contractorQuotes.map((quote) => (
                    <TableRow key={quote.contractor_quote_id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{quote.contractor_name}</span>
                          {quote.contractor_preferred && (
                            <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">Tier {quote.contractor_tier || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-blue-600">
                        £{quote.quote_total?.toFixed(2)}
                      </TableCell>
                      <TableCell className="font-mono font-semibold text-[#0F766E]">
                        £{quote.pricing?.client_price?.toFixed(2)}
                      </TableCell>
                      <TableCell className="font-mono text-amber-600">
                        £{quote.pricing?.markup_amount?.toFixed(2)}
                      </TableCell>
                      <TableCell>{quote.lead_time || '-'}</TableCell>
                      <TableCell>{quote.warranty_period || '-'}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm"
                          variant="outline"
                          onClick={() => handleCreateOption(quote)}
                          disabled={createdOptions.length >= 2 || createdOptions.some(o => o.contractor_quote_id === quote.contractor_quote_id)}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Create Option
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-gray-500 text-center py-6">No quotes received yet</p>
            )}
          </CardContent>
        </Card>

        {/* Created Options */}
        {createdOptions.length > 0 && (
          <Card className="border-[#0F766E]">
            <CardHeader className="pb-2 bg-[#0F766E]/5">
              <CardTitle className="text-base flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-[#0F766E]" />
                Member-Facing Options ({createdOptions.length}/2)
              </CardTitle>
              <CardDescription>These will be shown to the member (no contractor info visible)</CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                {createdOptions.map((opt) => (
                  <div key={opt.option_id} className="p-4 border rounded-lg bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-[#0F766E] text-white">Option {opt.option_id}</Badge>
                      {opt.badge && <Badge variant="outline">{opt.badge}</Badge>}
                    </div>
                    <h4 className="font-semibold">{opt.title}</h4>
                    <p className="text-sm text-gray-500 mt-1">{opt.description || 'No description'}</p>
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Client sees:</span>
                        <span className="font-bold text-[#0F766E]">£{opt.client_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>Internal (hidden):</span>
                        <span>£{opt.internal_cost?.toFixed(2)} + 25%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={handleCreatePHSQuote}
                disabled={creatingQuote}
                className="w-full bg-[#0F766E] hover:bg-[#115E59]"
              >
                {creatingQuote ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Create PHS Quote ({createdOptions.length} Option{createdOptions.length > 1 ? 's' : ''})
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Invite Contractors Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Invite Contractors</DialogTitle>
            </DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {availableContractors.length > 0 ? (
                availableContractors.map((c) => (
                  <div 
                    key={c.id || c.name}
                    className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      const id = c.id || c.name;
                      setSelectedContractors(prev => 
                        prev.includes(id) 
                          ? prev.filter(x => x !== id)
                          : [...prev, id]
                      );
                    }}
                  >
                    <Checkbox checked={selectedContractors.includes(c.id || c.name)} />
                    <div className="flex-1">
                      <p className="font-medium">{c.name || c.company_name}</p>
                      <p className="text-xs text-gray-500">{c.trade} • {c.region}</p>
                    </div>
                    {c.preferred && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">All contractors already invited</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleInviteContractors}
                disabled={inviting || selectedContractors.length === 0}
                className="bg-[#0F766E] hover:bg-[#115E59]"
              >
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Invite {selectedContractors.length > 0 ? `(${selectedContractors.length})` : ''}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Option Dialog */}
        <Dialog open={showCreateOptionDialog} onOpenChange={setShowCreateOptionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Member-Facing Option</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>From:</strong> {selectedQuoteForOption?.contractor_name}<br />
                  <strong>Cost:</strong> £{selectedQuoteForOption?.quote_total?.toFixed(2)} → 
                  <strong> Client Price:</strong> £{selectedQuoteForOption?.pricing?.client_price?.toFixed(2)}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Option ID</Label>
                  <Input 
                    value={optionForm.option_id}
                    onChange={(e) => setOptionForm({...optionForm, option_id: e.target.value.toUpperCase()})}
                    maxLength={1}
                    className="text-center font-bold"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Badge (Optional)</Label>
                  <Input 
                    value={optionForm.badge}
                    onChange={(e) => setOptionForm({...optionForm, badge: e.target.value})}
                    placeholder="e.g., Best Value"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Title *</Label>
                <Input 
                  value={optionForm.title}
                  onChange={(e) => setOptionForm({...optionForm, title: e.target.value})}
                  placeholder="e.g., Standard Repair Solution"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={optionForm.description}
                  onChange={(e) => setOptionForm({...optionForm, description: e.target.value})}
                  placeholder="Brief description of this option..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateOptionDialog(false)}>Cancel</Button>
              <Button onClick={handleAddOption} className="bg-[#0F766E] hover:bg-[#115E59]">
                <Plus className="h-4 w-4 mr-1" />
                Add Option
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
